import { AppColors } from "@/styles/colors";
import {
  ConditionalValue,
  createListCollection,
  Field,
  Select,
  Spinner,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import {
  Control,
  Controller,
  FieldError,
  FieldErrorsImpl,
  FieldValues,
  Merge,
} from "react-hook-form";

interface Props {
  items: any[];
  labelText?: string;
  placeholder?: string;
  control?: Control<FieldValues>;
  error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>>;
  multiple?: boolean;
  name: string;
  closeOnSelect?: boolean;
  clearable?: boolean;
  loading?: boolean;
  disabled?: boolean;
  required?: boolean;
  maxWidth?: string;
  size?: ConditionalValue<"sm" | "md" | "lg" | "xs">;
  handleChange?: (e: any) => void;
  fullWidth?: boolean;
  minHeight?: string;
  menuMaxHeight?:
    | string
    | { xs: string; sm: string; md: string; lg: string; xl: string };
}

export const SelectComponent = ({
  items,
  multiple = false,
  name,
  labelText,
  placeholder,
  control,
  closeOnSelect = true,
  error,
  clearable = true,
  loading,
  disabled,
  maxWidth,
  fullWidth,
  required,
  minHeight,
  menuMaxHeight,
  size,
  handleChange,
}: Props) => {
  
  const itemsToUse = createListCollection({
    items: items || [],
  });

  const [isXS, isSM, isMD, isLG, isXL] = useMediaQuery([
    "(max-width: 1350px)",
    "(min-width: 1351px) and (max-width: 1414px)",
    "(min-width: 1415px) and (max-width: 1590px)",
    "(min-width: 1591px) and (max-width: 1696px)",
    "(min-width: 1900px)",
  ]);

  const getSelectMaxH = () => {
    if (typeof menuMaxHeight === "string") return menuMaxHeight;
    if (typeof menuMaxHeight === "object" && isXS) return menuMaxHeight?.xs;
    if (typeof menuMaxHeight === "object" && isSM) return menuMaxHeight?.sm;
    if (typeof menuMaxHeight === "object" && isMD) return menuMaxHeight?.md;
    if (typeof menuMaxHeight === "object" && isLG) return menuMaxHeight?.lg;
    if (typeof menuMaxHeight === "object" && isXL) return menuMaxHeight?.xl;
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Field.Root invalid={!!error}>
          <Select.Root
           data-testid="select-component"
            multiple={multiple}
            collection={itemsToUse}
            size={size ?? "sm"}
            maxWidth={maxWidth ?? (fullWidth ? "" : "320px")}
            {...(fullWidth && {
              width: "100%",
            })}
            onValueChange={({ value }) => {
              field.onChange(value);
              handleChange?.(value);
            }}
            onInteractOutside={() => field.onBlur()}
            value={field.value}
            closeOnSelect={closeOnSelect}
            disabled={disabled}
          >
            <Select.HiddenSelect />
            <Select.Label>
              <Text display="flex" gap="4px" fontWeight={600}>
                {labelText}{" "}
                {required && (
                  <Text as="span" fontWeight={600} color={AppColors.REQUIRED}>
                    *
                  </Text>
                )}
              </Text>
            </Select.Label>
            <Select.Control>
              <Select.Trigger minH={minHeight}>
                <Select.ValueText paddingX="12px" placeholder={placeholder}/>
              </Select.Trigger>
              <Select.IndicatorGroup paddingX="12px">
                {clearable && <Select.ClearTrigger />}
                {loading && (
                  <Spinner size="xs" borderWidth="1.5px" color="fg.muted" />
                )}
                <Select.Indicator />
              </Select.IndicatorGroup>
            </Select.Control>

            <Select.Positioner width="100%">
              <Select.Content maxHeight={getSelectMaxH()}>
                {itemsToUse.items && itemsToUse.items.length > 0 ? (
                  itemsToUse.items.map((item, index) => (
                    <Select.Item
                      paddingX="12px"
                      paddingY="2px"
                      item={item}
                      key={item?.value ?? index}
                      data-testid={`select-content-${index}`}
                      {...(Boolean((item as any)?.disabled)
                        ? {
                            css: {
                              opacity: 0.5,
                              pointerEvents: "none",
                            },
                          }
                        : {})}
                    >
                      <Text
                        css={{
                          "@media (max-width: 1350px) and (min-width: 1272px)": {
                            fontSize: "10px",
                          },
                          "@media (max-width: 1414px) and (min-width: 1351px)": {
                            fontSize: "12px",
                          },
                          "@media (max-width: 1590px) and (min-width: 1415px)": {
                            fontSize: "16px",
                          },
                          "@media (max-width: 1696px) and (min-width: 1591px)": {
                            fontSize: "16px",
                          },
                          "@media (max-width: 1900px)": {
                            fontSize: "20px",
                          },
                        }}
                      >
                        {item?.label}
                      </Text>
                      <Select.ItemIndicator />
                    </Select.Item>
                  ))
                ) : (
                  <Select.Item paddingX="12px" paddingY="2px" item={{ label: "No hay opciones disponibles", value: "" }}>
                    <Text>No hay opciones disponibles</Text>
                  </Select.Item>
                )}
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
          {!!error && (
            <Field.ErrorText>
              {String(error?.message as string)}
            </Field.ErrorText>
          )}
        </Field.Root>
      )}
    />
  );
};
